import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  getAccount,
  testAccountConnection,
  listAccountModels,
  type Account,
  type TestConnectionResult,
  type BedrockModel,
} from '../../api/accounts';
import { listModels, type Model } from '../../api/models';
import { StatusBadge } from '../common/StatusBadge';

/**
 * Format a date string for display, or return a dash if unavailable.
 */
function formatDate(dateStr?: string): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
}

/**
 * Extract accountId from a role ARN.
 */
function extractAccountIdFromArn(arn: string): string | null {
  const match = arn.match(/arn:aws:iam::(\d{12}):role\//);
  return match ? match[1] : null;
}

export function AccountDetailView() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const { getJwtToken } = useAuth();

  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Test connection
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);

  // Bedrock models in this account
  const [bedrockModels, setBedrockModels] = useState<BedrockModel[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);

  // Configured deployments using this account
  const [deployments, setDeployments] = useState<Model[]>([]);

  const fetchAccount = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const data = await getAccount(accountId, token);
      setAccount(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load account');
    } finally {
      setLoading(false);
    }
  }, [accountId, getJwtToken]);

  const fetchDeployments = useCallback(async () => {
    if (!accountId) return;
    try {
      const token = await getJwtToken();
      if (!token) return;
      const allModels = await listModels(token);
      const accountModels = allModels.filter((m) => {
        const roleArn = m.litellm_params?.aws_role_name as string | undefined;
        if (!roleArn) return false;
        return extractAccountIdFromArn(roleArn) === accountId;
      });
      setDeployments(accountModels);
    } catch {
      // Non-critical, don't show error
    }
  }, [accountId, getJwtToken]);

  useEffect(() => {
    fetchAccount();
    fetchDeployments();
  }, [fetchAccount, fetchDeployments]);

  const handleTestConnection = async () => {
    if (!accountId) return;
    setTesting(true);
    setTestResult(null);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const result = await testAccountConnection(accountId, token);
      setTestResult(result);
      // Refresh account to get updated lastTestedAt
      await fetchAccount();
    } catch (err) {
      setTestResult({
        success: false,
        error: err instanceof Error ? err.message : 'Connection test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleLoadModels = async () => {
    if (!accountId) return;
    setLoadingModels(true);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const models = await listAccountModels(accountId, token);
      setBedrockModels(models);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Bedrock models');
    } finally {
      setLoadingModels(false);
    }
  };

  if (loading) {
    return <div className="view__empty">Loading account...</div>;
  }

  if (error && !account) {
    return (
      <div>
        <div className="alert alert--error">
          <span>{error}</span>
        </div>
        <button
          className="btn btn--secondary"
          onClick={() => navigate('/admin/accounts')}
          type="button"
        >
          Back to Accounts
        </button>
      </div>
    );
  }

  if (!account) {
    return <div className="view__empty">Account not found.</div>;
  }

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">{account.accountName}</h1>
        <div className="view__actions">
          <button
            className="btn btn--secondary"
            onClick={() => navigate('/admin/accounts')}
            type="button"
          >
            Back
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">
            Dismiss
          </button>
        </div>
      )}

      {/* Account Info Card */}
      <div className="settings-card">
        <h2 className="settings-card__title">Account Information</h2>
        <div className="settings-card__body">
          <div className="models-view__detail-row">
            <span className="models-view__detail-label">Account ID:</span>
            <code>{account.accountId}</code>
          </div>
          <div className="models-view__detail-row">
            <span className="models-view__detail-label">Name:</span>
            <span>{account.accountName}</span>
          </div>
          <div className="models-view__detail-row">
            <span className="models-view__detail-label">Role ARN:</span>
            <code>{account.roleArn}</code>
          </div>
          {account.externalId && (
            <div className="models-view__detail-row">
              <span className="models-view__detail-label">External ID:</span>
              <code>{account.externalId}</code>
            </div>
          )}
          <div className="models-view__detail-row">
            <span className="models-view__detail-label">Region:</span>
            <span>{account.region}</span>
          </div>
          <div className="models-view__detail-row">
            <span className="models-view__detail-label">Status:</span>
            <StatusBadge status={account.status} />
          </div>
          {account.tpmLimit !== undefined && account.tpmLimit !== null && (
            <div className="models-view__detail-row">
              <span className="models-view__detail-label">TPM Limit:</span>
              <span>{account.tpmLimit.toLocaleString()}</span>
            </div>
          )}
          {account.rpmLimit !== undefined && account.rpmLimit !== null && (
            <div className="models-view__detail-row">
              <span className="models-view__detail-label">RPM Limit:</span>
              <span>{account.rpmLimit.toLocaleString()}</span>
            </div>
          )}
          <div className="models-view__detail-row">
            <span className="models-view__detail-label">Last Tested:</span>
            <span>{formatDate(account.lastTestedAt)}</span>
          </div>
          {account.lastTestResult && (
            <div className="models-view__detail-row">
              <span className="models-view__detail-label">Last Test Result:</span>
              <StatusBadge status={account.lastTestResult === 'success' ? 'active' : 'disabled'} />
            </div>
          )}
          {account.lastTestError && (
            <div className="models-view__detail-row">
              <span className="models-view__detail-label">Last Test Error:</span>
              <span className="alert--error" style={{ padding: '4px 8px', fontSize: '0.85em' }}>
                {account.lastTestError}
              </span>
            </div>
          )}
          <div className="models-view__detail-row">
            <span className="models-view__detail-label">Created:</span>
            <span>{formatDate(account.createdAt)}</span>
          </div>
          <div className="models-view__detail-row">
            <span className="models-view__detail-label">Created By:</span>
            <span>{account.createdBy}</span>
          </div>
        </div>
      </div>

      {/* Test Connection */}
      <div className="settings-card" style={{ marginTop: 24 }}>
        <h2 className="settings-card__title">Connection Test</h2>
        <div className="settings-card__body">
          <button
            className="btn btn--primary"
            onClick={handleTestConnection}
            disabled={testing}
            type="button"
          >
            {testing ? 'Testing...' : 'Test Connection'}
          </button>

          {testResult && (
            <div style={{ marginTop: 16 }}>
              {testResult.success ? (
                <div className="alert alert--success">
                  <span>Connection successful. {testResult.availableModels?.length ?? 0} models available.</span>
                </div>
              ) : (
                <div className="alert alert--error">
                  <span>Connection failed: {testResult.error}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Available Bedrock Models */}
      <div className="settings-card" style={{ marginTop: 24 }}>
        <h2 className="settings-card__title">Available Bedrock Models (Anthropic)</h2>
        <div className="settings-card__body">
          <button
            className="btn btn--secondary"
            onClick={handleLoadModels}
            disabled={loadingModels}
            type="button"
          >
            {loadingModels ? 'Loading...' : 'Load Models'}
          </button>

          {bedrockModels.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="data-table__th">Model ID</th>
                    <th className="data-table__th">Name</th>
                    <th className="data-table__th">Inference Types</th>
                  </tr>
                </thead>
                <tbody>
                  {bedrockModels.map((m) => (
                    <tr key={m.modelId} className="data-table__tr">
                      <td className="data-table__td"><code>{m.modelId}</code></td>
                      <td className="data-table__td">{m.modelName}</td>
                      <td className="data-table__td">{m.inferenceTypesSupported?.join(', ') ?? '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Configured Deployments */}
      <div className="settings-card" style={{ marginTop: 24 }}>
        <h2 className="settings-card__title">Configured Model Deployments</h2>
        <div className="settings-card__body">
          {deployments.length === 0 ? (
            <p className="view__empty">No model deployments currently using this account.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th className="data-table__th">Model Name</th>
                  <th className="data-table__th">Provider Model</th>
                </tr>
              </thead>
              <tbody>
                {deployments.map((m) => (
                  <tr key={m.model_info?.id as string ?? m.model_name} className="data-table__tr">
                    <td className="data-table__td"><strong>{m.model_name}</strong></td>
                    <td className="data-table__td"><code>{m.litellm_params?.model ?? '--'}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
